'use client';

import {
  Typography,
  TypographyMods,
  TypographySizes,
} from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';
import Link from 'next/link';
import { useState } from 'react';

import { CardDraftLabel } from '~components/CardDraftLabel/CardDraftLabel';
import { NextImage } from '~components/NextImage/NextImage';
import { NextImageMods } from '~components/NextImage/NextImage.types';
import { getMonthDayDate } from '~helpers/date/getDate';
import LinkIcon from '~public/static/images/icons/arrows/Link.svg';
import {
  ArticleCardImagePosition,
  ArticleCardMods,
} from '~types/Article.types';

import styles from './ArticleCardAsymmetric.module.scss';
import { ArticleCardAsymmetricProps } from './ArticleCardAsymmetric.types';

export function ArticleCardAsymmetric({
  className,
  title,
  text,
  categories,
  image,
  href,
  imagePosition = ArticleCardImagePosition.Top,
  imageWithBg,
  mods,
  publishedAt,
  showDate = false,
}: ArticleCardAsymmetricProps) {
  const renderCategories = () =>
    !!categories?.length && (
      <div className={styles.categoryList}>
        {categories.map((category) => (
          <Typography
            tagName={TypographySizes.div}
            mods={[TypographyMods.captionSAlt]}
            key={category.id}
            className={styles.category}
          >
            {category.title}
          </Typography>
        ))}
        {showDate && (
          <div key="article-date-id" className={styles.category}>
            {getMonthDayDate(new Date(publishedAt))}
          </div>
        )}
      </div>
    );

  const isWide = mods?.includes(ArticleCardMods.Wide);

  const [isHover, setIsHover] = useState(false);

  return (
    <article
      className={classNames(
        className,
        styles.card,
        isHover && styles.hovered,
        imageWithBg && styles.imageWithBg,
        imagePosition === ArticleCardImagePosition.Bottom &&
          styles.imagePositionBottom,
        mods?.map((mod) => styles[mod]),
      )}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {!publishedAt && <CardDraftLabel />}
      {image && (
        <div className={styles.media}>
          <NextImage
            className={styles.image}
            mods={!imageWithBg ? [NextImageMods.FitContain] : []}
            {...image}
          />
        </div>
      )}

      {imagePosition === ArticleCardImagePosition.Top && renderCategories()}

      <div className={styles.content}>
        <div className={styles.contentText}>
          <Typography
            className={styles.title}
            tagName={TypographySizes.h3}
            mods={isWide ? [TypographyMods.titleXl] : [TypographyMods.titleL]}
          >
            {title}
          </Typography>
          {text && !isWide && (
            <Typography
              tagName={TypographySizes.p}
              mods={[TypographyMods.bodyS]}
              className={styles.text}
            >
              {text}
            </Typography>
          )}
          {imagePosition === ArticleCardImagePosition.Bottom &&
            renderCategories()}
        </div>
        <div className={styles.contentLink}>
          <Link className={styles.link} href={href}>
            {isWide && <LinkIcon className={styles.linkIcon} />}
          </Link>
        </div>
      </div>
    </article>
  );
}
